import { isApiResponse, type ApiResponse } from "@m8t-stack/api-contract";
import { ApiCallError, LocalCliError } from "./errors.js";
import { getBearerToken } from "./auth.js";

export interface ApiClientOptions {
  gatewayUrl: string;
  gatewayClientId: string;
}

export interface ApiRequest {
  method: "GET" | "POST" | "PATCH" | "DELETE";
  path: string;
  body?: unknown;
}

const COLD_START_RETRY_DELAY_MS = 1000;
const COLD_START_RETRY_STATUS_CODES = new Set([502, 503, 504]);

export async function apiCall<T>(
  opts: ApiClientOptions,
  req: ApiRequest,
  notify?: (msg: string) => void,
): Promise<T> {
  const token = await getBearerToken(opts.gatewayClientId);

  const url = `${opts.gatewayUrl}${req.path}`;
  const headers: Record<string, string> = {
    authorization: `Bearer ${token}`,
    accept: "application/json",
  };
  if (req.body !== undefined) headers["content-type"] = "application/json";

  const init: RequestInit = {
    method: req.method,
    headers,
    body: req.body !== undefined ? JSON.stringify(req.body) : undefined,
  };

  let res: Response;
  try {
    res = await fetch(url, init);
  } catch (e) {
    throw new LocalCliError({
      code: "NETWORK_FAILED",
      message: `Could not reach gateway at ${opts.gatewayUrl}: ${(e as Error).message}`,
      hint: "Verify the gateway is deployed + reachable. 'm8t config reset' to re-discover.",
      cause: e,
    });
  }

  if (COLD_START_RETRY_STATUS_CODES.has(res.status)) {
    notify?.("(gateway is starting up...)");
    await new Promise((r) => setTimeout(r, COLD_START_RETRY_DELAY_MS));
    try {
      res = await fetch(url, init);
    } catch (e) {
      throw new LocalCliError({
        code: "NETWORK_FAILED",
        message: `Gateway unreachable after retry: ${(e as Error).message}`,
        hint: "Verify the gateway is deployed + reachable. 'm8t config reset' to re-discover.",
        cause: e,
      });
    }
  }

  let parsed: unknown;
  try {
    parsed = await res.json();
  } catch (e) {
    throw new LocalCliError({
      code: "BAD_RESPONSE",
      message: `Gateway returned non-JSON (HTTP ${res.status.toString()}).`,
      hint: "Run with --verbose to see the raw response.",
      cause: e,
    });
  }

  if (!isApiResponse(parsed)) {
    throw new LocalCliError({
      code: "BAD_RESPONSE",
      message: `Gateway returned a response that doesn't match ApiResponse shape (HTTP ${res.status.toString()}).`,
      hint: "This may be a gateway version mismatch.",
    });
  }
  const envelope = parsed as ApiResponse<T>;

  if (!envelope.ok) {
    throw new ApiCallError(envelope.error, res.status);
  }

  return envelope.data;
}
